import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Facebook, 
  Users,
  Image,
  Calculator,
  BarChart3,
  Shuffle,
  Target,
  ArrowLeft,
  RefreshCw,
  Copy,
  CheckCircle2,
  DollarSign,
  TrendingUp,
  MousePointer,
  Eye,
  Loader2,
  AlertCircle,
  LogIn,
  Link2,
  Download,
  Video,
  ImageIcon,
  ExternalLink,
  Construction
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import RelatedTools from "@/components/RelatedTools";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Tabs that are coming soon (need OAuth to work)
const COMING_SOON_TABS = ["campaign-manager"];

function FacebookAdsToolsContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTool, setActiveTool] = useState("ad-creative-downloader");

  // Get tool from URL params
  useEffect(() => {
    const toolFromUrl = searchParams.get('tool');
    if (toolFromUrl) {
      setActiveTool(toolFromUrl);
    }
  }, [searchParams]);

  // Dashboard State
  const [dashboardData, setDashboardData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [insights, setInsights] = useState(null);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [datePreset, setDatePreset] = useState("last_7d");
  const [isDemo, setIsDemo] = useState(false);
  
  // Facebook Connection State
  const [fbConnected, setFbConnected] = useState(false);
  const [fbAdAccounts, setFbAdAccounts] = useState([]);
  const [connectingFb, setConnectingFb] = useState(false);

  // Audience Builder State
  const [product, setProduct] = useState("");
  const [targetAge, setTargetAge] = useState("25-44");
  const [interests, setInterests] = useState("");
  const [audienceResult, setAudienceResult] = useState(null);
  const [loadingAudience, setLoadingAudience] = useState(false);

  // Ad Creative Generator State
  const [adProduct, setAdProduct] = useState("");
  const [adObjective, setAdObjective] = useState("conversions");
  const [generatedCreative, setGeneratedCreative] = useState(null);
  const [loadingCreative, setLoadingCreative] = useState(false);

  // ROI Calculator State
  const [adSpend, setAdSpend] = useState("");
  const [revenue, setRevenue] = useState("");
  const [roiResult, setRoiResult] = useState(null);

  // API Test State (For Meta App Review)
  const [testToken, setTestToken] = useState("");
  const [testAdAccountId, setTestAdAccountId] = useState("");
  const [testResults, setTestResults] = useState(null);
  const [testingApi, setTestingApi] = useState(false);

  // Meta Ads Creative Downloader State
  const [metaAdUrl, setMetaAdUrl] = useState("");
  const [metaAdResult, setMetaAdResult] = useState(null);
  const [loadingMetaAd, setLoadingMetaAd] = useState(false);
  const [metaAdError, setMetaAdError] = useState(null);

  // Check Facebook connection on mount
  useEffect(() => {
    checkFacebookConnection();
    fetchDashboardData();
  }, []);
  
  // Handle OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      handleFacebookCallback(code);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Fetch insights when date range changes
  useEffect(() => {
    if (activeTool === "campaign-manager") {
      fetchInsights();
    }
  }, [datePreset, activeTool]);
  
  const checkFacebookConnection = async () => {
    try {
      const response = await axios.get(`${API}/auth/facebook/status`, { withCredentials: true, headers: { 'X-Skip-Auth-Error': 'true' } });
      setFbConnected(response.data.connected);
      setFbAdAccounts(response.data.ad_accounts || []);
    } catch (error) {
      console.error("FB status check error:", error);
    }
  };
  
  const handleFacebookLogin = async () => {
    setConnectingFb(true);
    try {
      const redirectUri = window.location.origin + window.location.pathname;
      const response = await axios.get(`${API}/auth/facebook/login-url`, {
        params: { redirect_uri: redirectUri }
      });
      
      if (response.data.login_url) {
        window.location.href = response.data.login_url;
      }
    } catch (error) {
      toast.error("Failed to start Facebook login");
      setConnectingFb(false);
    }
  };
  
  const handleFacebookCallback = async (code) => {
    setConnectingFb(true);
    try {
      const redirectUri = window.location.origin + window.location.pathname;
      const response = await axios.post(`${API}/auth/facebook/callback`, {
        code,
        redirect_uri: redirectUri
      }, { withCredentials: true });
      
      if (response.data.success) {
        toast.success("Facebook connected successfully!");
        setFbConnected(true);
        setFbAdAccounts(response.data.ad_accounts || []);
        // Refresh dashboard data with real FB data
        fetchDashboardData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect Facebook");
    } finally {
      setConnectingFb(false);
    }
  };

  const fetchDashboardData = async () => {
    setLoadingDashboard(true);
    try {
      const response = await axios.get(`${API}/facebook-ads/dashboard`, { withCredentials: true, headers: { 'X-Skip-Auth-Error': 'true' } });
      setDashboardData(response.data);
      setCampaigns(response.data.recent_campaigns || []);
      // Check is_demo from account object or root level
      setIsDemo(response.data.is_demo || response.data.account?.is_demo || false);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast.error("Could not load Facebook Ads data");
    } finally {
      setLoadingDashboard(false);
    }
  };

  const fetchInsights = async () => {
    try {
      const response = await axios.get(`${API}/facebook-ads/insights`, {
        params: { date_preset: datePreset, level: "campaign" }
      });
      setInsights(response.data.data?.[0] || null);
      setIsDemo(response.data.is_demo || false);
    } catch (error) {
      console.error("Insights fetch error:", error);
    }
  };

  const fetchCampaigns = async () => {
    setLoadingDashboard(true);
    try {
      const response = await axios.get(`${API}/facebook-ads/campaigns`);
      setCampaigns(response.data.data || []);
      setIsDemo(response.data.is_demo || false);
      toast.success("Campaigns refreshed!");
    } catch (error) {
      toast.error("Failed to fetch campaigns");
    } finally {
      setLoadingDashboard(false);
    }
  };

  // Test Facebook Marketing API (For Meta App Review)
  const testFacebookApi = async () => {
    if (!testToken.trim()) {
      toast.error("Please enter your Facebook Access Token");
      return;
    }
    
    setTestingApi(true);
    setTestResults(null);
    
    try {
      const response = await axios.post(`${API}/facebook-ads/test-api-call`, {
        access_token: testToken,
        ad_account_id: testAdAccountId || null
      });
      
      setTestResults(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`${response.data.success_count} API calls successful! Meta will count these.`);
      } else {
        toast.error("All API calls failed. Check your token permissions.");
      }
    } catch (error) {
      toast.error("Test failed: " + (error.response?.data?.detail || error.message));
      setTestResults({ error: error.message });
    } finally {
      setTestingApi(false);
    }
  };

  const facebookAdsTools = [
    {
      id: "audience-builder",
      name: "Audience Builder",
      icon: <Users className="w-5 h-5" />,
      description: "Build targeted audience segments"
    },
    {
      id: "ad-creative",
      name: "Ad Creative Generator",
      icon: <Image className="w-5 h-5" />,
      description: "Generate compelling ad creatives"
    },
    {
      id: "roi-calculator",
      name: "ROI Calculator",
      icon: <Calculator className="w-5 h-5" />,
      description: "Calculate return on ad spend"
    },
    {
      id: "campaign-manager",
      name: "Campaign Manager",
      icon: <BarChart3 className="w-5 h-5" />,
      description: "Manage your ad campaigns"
    },
    {
      id: "ab-testing",
      name: "A/B Testing Tool",
      icon: <Shuffle className="w-5 h-5" />,
      description: "Test different ad variations"
    },
    {
      id: "conversion-tracking",
      name: "Conversion Tracking",
      icon: <Target className="w-5 h-5" />,
      description: "Track ad conversions"
    },
    {
      id: "api-test",
      name: "API Test Tool",
      icon: <RefreshCw className="w-5 h-5" />,
      description: "Test Marketing API (For Meta Review)"
    },
    {
      id: "meta-ad-downloader",
      name: "Ad Creative Downloader",
      icon: <Download className="w-5 h-5" />,
      description: "Download Meta Ads Library creatives"
    }
  ];

  const handleBuildAudience = async () => {
    if (!product.trim()) {
      toast.error("Please enter your product/service");
      return;
    }

    setLoadingAudience(true);
    try {
      const response = await axios.post(`${API}/content/generate`, {
        topic: `Create a detailed Facebook Ads audience targeting strategy for: ${product}. 
        Target age: ${targetAge}
        Additional interests: ${interests || 'None specified'}
        
        Include:
        1. Core audience demographics
        2. Interest targeting suggestions (5-10 specific interests)
        3. Behavior targeting suggestions
        4. Lookalike audience recommendations
        5. Exclusion recommendations
        6. Estimated audience size and reach`,
        content_type: "blog_post",
        tone: "professional",
        keywords: [product],
        length: "medium"
      });
      
      setAudienceResult({
        product,
        strategy: response.data.content
      });
      toast.success("Audience strategy generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate audience");
    } finally {
      setLoadingAudience(false);
    }
  };

  const handleGenerateCreative = async () => {
    if (!adProduct.trim()) {
      toast.error("Please enter your product/service");
      return;
    }

    setLoadingCreative(true);
    try {
      const response = await axios.post(`${API}/content/generate`, {
        topic: `Create Facebook Ad creative concepts for: ${adProduct}
        Campaign Objective: ${adObjective}
        
        Generate:
        1. 3 different ad headline options (max 40 chars each)
        2. 3 primary text options (max 125 chars each)
        3. 3 description options (max 30 chars each)
        4. 3 call-to-action button suggestions
        5. Image/video creative direction suggestions
        6. Ad placement recommendations (Feed, Stories, Reels)`,
        content_type: "email",
        tone: "persuasive",
        keywords: [adProduct],
        length: "medium"
      });
      
      setGeneratedCreative({
        product: adProduct,
        objective: adObjective,
        content: response.data.content
      });
      toast.success("Ad creative generated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate creative");
    } finally {
      setLoadingCreative(false);
    }
  };

  const calculateROI = () => {
    const spend = parseFloat(adSpend);
    const rev = parseFloat(revenue);

    if (!spend || !rev) {
      toast.error("Please enter ad spend and revenue");
      return;
    }

    const profit = rev - spend;
    const roi = ((rev - spend) / spend) * 100;
    const roas = rev / spend;

    setRoiResult({
      adSpend: spend,
      revenue: rev,
      profit: profit.toFixed(2),
      roi: roi.toFixed(2),
      roas: roas.toFixed(2)
    });
    toast.success("ROI calculated!");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // Meta Ads Creative Downloader Handler
  const handleMetaAdDownload = async () => {
    if (!metaAdUrl.trim()) {
      toast.error("Please enter a Meta Ads Library URL");
      return;
    }

    // Validate URL format
    if (!metaAdUrl.includes('facebook.com/ads/library') && !metaAdUrl.includes('fb.com/ads/library')) {
      toast.error("Please enter a valid Meta Ads Library URL");
      setMetaAdError("Invalid URL format. The URL should be from facebook.com/ads/library");
      return;
    }

    setLoadingMetaAd(true);
    setMetaAdResult(null);
    setMetaAdError(null);

    try {
      const response = await axios.post(`${API}/facebook-ads/meta-ads-creative`, {
        url: metaAdUrl
      }, { withCredentials: true, timeout: 60000 }); // 60 seconds for Playwright scraping

      if (response.data.success) {
        setMetaAdResult(response.data);
        toast.success(`${response.data.media_type === 'video' ? 'Video' : 'Image'} creative found!`);
      } else {
        setMetaAdError(response.data.error || "Could not extract creative from this ad");
        toast.error(response.data.error || "Extraction failed");
      }
    } catch (error) {
      let errorMsg = "Failed to extract ad creative";
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg = "Request timed out. The ad page is taking too long to load. Please try again.";
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      setMetaAdError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoadingMetaAd(false);
    }
  };

  const handleDownloadCreative = async () => {
    if (!metaAdResult?.media_url) return;
    
    try {
      // Use backend proxy for download (avoids CORS)
      const downloadUrl = `${API}/facebook-ads/meta-ads-creative/download?url=${encodeURIComponent(metaAdResult.media_url)}&media_type=${metaAdResult.media_type}`;
      
      const filename = `meta_ad_${Date.now()}.${metaAdResult.media_type === 'video' ? 'mp4' : 'jpg'}`;
      
      if (metaAdResult.media_type === 'video') {
        // For videos, use fetch with blob to handle large files properly
        toast.info("Downloading video... Please wait, this may take a minute for large files.");
        
        try {
          const response = await fetch(downloadUrl);
          
          if (!response.ok) {
            throw new Error(`Download failed: ${response.status}`);
          }
          
          // Get total size for progress (if available)
          const contentLength = response.headers.get('content-length');
          const total = contentLength ? parseInt(contentLength, 10) : 0;
          
          // Read the stream
          const reader = response.body.getReader();
          const chunks = [];
          let receivedLength = 0;
          
          while(true) {
            const {done, value} = await reader.read();
            
            if (done) break;
            
            chunks.push(value);
            receivedLength += value.length;
            
            // Log progress for large files
            if (total > 0 && receivedLength % (1024 * 1024) < 65536) {
              const percent = Math.round((receivedLength / total) * 100);
              console.log(`Download progress: ${percent}%`);
            }
          }
          
          // Combine chunks into blob
          const blob = new Blob(chunks, { type: 'video/mp4' });
          const blobUrl = URL.createObjectURL(blob);
          
          // Trigger download
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Cleanup blob URL
          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          
          toast.success(`Video downloaded! (${(receivedLength / (1024 * 1024)).toFixed(1)} MB)`);
          
        } catch (fetchError) {
          console.error("Fetch download error:", fetchError);
          // Fallback to direct link
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filename;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.info("Download started in browser!");
        }
      } else {
        // For images, simple anchor link works
        toast.info("Downloading image...");
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Image download started!");
      }
      
    } catch (error) {
      console.error("Download error:", error);
      // Fallback: open URL directly
      window.open(metaAdResult.media_url, '_blank');
      toast.info("Opening in new tab - right-click to save");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">Facebook Ads Tools</h3>
              <div className="space-y-2">
                {facebookAdsTools.map((tool) => (
                  <button
                    key={tool.id}
                    onClick={() => !COMING_SOON_TABS.includes(tool.id) && setActiveTool(tool.id)}
                    data-testid={`tool-${tool.id}`}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                      COMING_SOON_TABS.includes(tool.id) 
                        ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                        : activeTool === tool.id
                          ? 'bg-blue-100 text-blue-700 font-medium'
                          : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {tool.icon}
                        <span className="text-sm">{tool.name}</span>
                      </div>
                      {COMING_SOON_TABS.includes(tool.id) && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Soon</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Audience Builder */}
            {activeTool === "audience-builder" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Audience Builder</h2>
                    <p className="text-gray-600">Build targeted audience segments for your ads</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Product/Service</label>
                    <Input
                      placeholder="e.g., Online fitness coaching"
                      value={product}
                      onChange={(e) => setProduct(e.target.value)}
                      data-testid="product-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Target Age Range</label>
                      <Select value={targetAge} onValueChange={setTargetAge}>
                        <SelectTrigger data-testid="age-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="18-24">18-24</SelectItem>
                          <SelectItem value="25-34">25-34</SelectItem>
                          <SelectItem value="25-44">25-44</SelectItem>
                          <SelectItem value="35-54">35-54</SelectItem>
                          <SelectItem value="45-65+">45-65+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Additional Interests</label>
                      <Input
                        placeholder="e.g., yoga, healthy eating"
                        value={interests}
                        onChange={(e) => setInterests(e.target.value)}
                        data-testid="interests-input"
                      />
                    </div>
                  </div>

                  <Button 
                    onClick={handleBuildAudience}
                    disabled={loadingAudience}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="build-audience-btn"
                  >
                    {loadingAudience ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Building...</>
                    ) : (
                      <><Users className="w-4 h-4 mr-2" /> Build Audience Strategy</>
                    )}
                  </Button>
                </div>

                {audienceResult && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">Audience Strategy</h3>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(audienceResult.strategy)}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                    </div>
                    <div className="p-4 bg-white border rounded-lg whitespace-pre-wrap">
                      {audienceResult.strategy}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Ad Creative Generator */}
            {activeTool === "ad-creative" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <Image className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Ad Creative Generator</h2>
                    <p className="text-gray-600">Generate compelling Facebook ad creatives</p>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Product/Service</label>
                    <Input
                      placeholder="e.g., Premium skincare products"
                      value={adProduct}
                      onChange={(e) => setAdProduct(e.target.value)}
                      data-testid="ad-product-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Campaign Objective</label>
                    <Select value={adObjective} onValueChange={setAdObjective}>
                      <SelectTrigger data-testid="objective-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="awareness">Brand Awareness</SelectItem>
                        <SelectItem value="traffic">Website Traffic</SelectItem>
                        <SelectItem value="engagement">Engagement</SelectItem>
                        <SelectItem value="leads">Lead Generation</SelectItem>
                        <SelectItem value="conversions">Conversions</SelectItem>
                        <SelectItem value="sales">Sales</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    onClick={handleGenerateCreative}
                    disabled={loadingCreative}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    data-testid="generate-creative-btn"
                  >
                    {loadingCreative ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Image className="w-4 h-4 mr-2" /> Generate Ad Creative</>
                    )}
                  </Button>
                </div>

                {generatedCreative && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="font-semibold">Ad Creative Ready</span>
                        <Badge>{generatedCreative.objective}</Badge>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => copyToClipboard(generatedCreative.content)}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                    </div>
                    <div className="p-4 bg-white border rounded-lg whitespace-pre-wrap">
                      {generatedCreative.content}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* ROI Calculator */}
            {activeTool === "roi-calculator" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                    <Calculator className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">ROI Calculator</h2>
                    <p className="text-gray-600">Calculate your Facebook Ads ROI and ROAS</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Total Ad Spend ($)</label>
                    <Input
                      type="number"
                      placeholder="e.g., 500"
                      value={adSpend}
                      onChange={(e) => setAdSpend(e.target.value)}
                      data-testid="ad-spend-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Total Revenue ($)</label>
                    <Input
                      type="number"
                      placeholder="e.g., 2000"
                      value={revenue}
                      onChange={(e) => setRevenue(e.target.value)}
                      data-testid="revenue-input"
                    />
                  </div>
                </div>

                <Button 
                  onClick={calculateROI}
                  className="w-full bg-green-600 hover:bg-green-700"
                  data-testid="calculate-roi-btn"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  Calculate ROI
                </Button>

                {roiResult && (
                  <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-red-50 rounded-lg text-center">
                      <p className="text-sm text-gray-600">Ad Spend</p>
                      <p className="text-2xl font-bold text-red-600">${roiResult.adSpend}</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-sm text-gray-600">Revenue</p>
                      <p className="text-2xl font-bold text-green-600">${roiResult.revenue}</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-sm text-gray-600">Profit</p>
                      <p className="text-2xl font-bold text-blue-600">${roiResult.profit}</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center col-span-2 md:col-span-1">
                      <p className="text-sm text-gray-600">ROI</p>
                      <p className={`text-2xl font-bold ${parseFloat(roiResult.roi) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {roiResult.roi}%
                      </p>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg text-center col-span-2 md:col-span-2">
                      <p className="text-sm text-gray-600">ROAS (Return on Ad Spend)</p>
                      <p className="text-2xl font-bold text-orange-600">{roiResult.roas}x</p>
                      <p className="text-xs text-gray-500 mt-1">For every $1 spent, you made ${roiResult.roas}</p>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Campaign Manager */}
            {activeTool === "campaign-manager" && (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Campaign Manager</h2>
                      <p className="text-gray-600">View and manage your Facebook ad campaigns</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isDemo && (
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Demo Data
                      </Badge>
                    )}
                    <Select value={datePreset} onValueChange={setDatePreset}>
                      <SelectTrigger className="w-[140px]" data-testid="date-preset-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="last_7d">Last 7 Days</SelectItem>
                        <SelectItem value="last_14d">Last 14 Days</SelectItem>
                        <SelectItem value="last_30d">Last 30 Days</SelectItem>
                        <SelectItem value="this_month">This Month</SelectItem>
                        <SelectItem value="last_month">Last Month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {loadingDashboard ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    <span className="ml-3 text-gray-600">Loading campaign data...</span>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Performance Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <BarChart3 className="w-4 h-4 text-blue-600" />
                          <p className="text-sm text-gray-600">Active Campaigns</p>
                        </div>
                        <p className="text-2xl font-bold text-blue-600">
                          {dashboardData?.summary?.active_campaigns || 0}
                        </p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <Eye className="w-4 h-4 text-green-600" />
                          <p className="text-sm text-gray-600">Total Reach</p>
                        </div>
                        <p className="text-2xl font-bold text-green-600">
                          {(dashboardData?.summary?.reach || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-orange-50 rounded-lg text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <DollarSign className="w-4 h-4 text-orange-600" />
                          <p className="text-sm text-gray-600">Total Spend</p>
                        </div>
                        <p className="text-2xl font-bold text-orange-600">
                          ₹{(dashboardData?.summary?.spend || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <Target className="w-4 h-4 text-purple-600" />
                          <p className="text-sm text-gray-600">Conversions</p>
                        </div>
                        <p className="text-2xl font-bold text-purple-600">
                          {dashboardData?.summary?.conversions || 0}
                        </p>
                      </div>
                    </div>

                    {/* Performance Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-xs text-gray-500">Impressions</p>
                        <p className="text-lg font-semibold text-gray-800">
                          {(dashboardData?.summary?.impressions || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-xs text-gray-500">Clicks</p>
                        <p className="text-lg font-semibold text-gray-800">
                          {(dashboardData?.summary?.clicks || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-xs text-gray-500">CTR</p>
                        <p className="text-lg font-semibold text-gray-800">
                          {(dashboardData?.summary?.ctr || 0).toFixed(2)}%
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-xs text-gray-500">CPC</p>
                        <p className="text-lg font-semibold text-gray-800">
                          ₹{(dashboardData?.summary?.cpc || 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg text-center">
                        <p className="text-xs text-gray-500">Conv. Value</p>
                        <p className="text-lg font-semibold text-green-600">
                          ₹{(dashboardData?.summary?.conversion_value || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {/* Campaigns List */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">Your Campaigns</h3>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={fetchCampaigns}
                          disabled={loadingDashboard}
                          data-testid="refresh-campaigns-btn"
                        >
                          <RefreshCw className={`w-4 h-4 mr-2 ${loadingDashboard ? 'animate-spin' : ''}`} />
                          Refresh
                        </Button>
                      </div>
                      
                      {campaigns.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          No campaigns found
                        </div>
                      ) : (
                        campaigns.map((campaign, idx) => (
                          <div key={campaign.id || idx} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium">{campaign.name}</h4>
                              <Badge className={
                                campaign.status === "ACTIVE" ? "bg-green-100 text-green-700" :
                                campaign.status === "PAUSED" ? "bg-yellow-100 text-yellow-700" :
                                "bg-gray-100 text-gray-700"
                              }>
                                {campaign.status}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                              <div>
                                <span className="text-gray-400">Objective:</span>{' '}
                                {campaign.objective?.replace(/_/g, ' ') || 'N/A'}
                              </div>
                              <div>
                                <span className="text-gray-400">Budget:</span>{' '}
                                ₹{campaign.daily_budget ? (parseInt(campaign.daily_budget)/100).toLocaleString() + '/day' : 
                                   campaign.lifetime_budget ? (parseInt(campaign.lifetime_budget)/100).toLocaleString() + ' lifetime' : 'N/A'}
                              </div>
                              <div>
                                <span className="text-gray-400">Created:</span>{' '}
                                {campaign.created_time ? new Date(campaign.created_time).toLocaleDateString() : 'N/A'}
                              </div>
                              <div>
                                <span className="text-gray-400">ID:</span>{' '}
                                <span className="font-mono text-xs">{campaign.id?.slice(0, 12)}...</span>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    {/* Facebook Connection Section */}
                    {isDemo ? (
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-blue-900">Connect Your Facebook Ads Account</h4>
                            <p className="text-sm text-blue-700">Link your account to see real campaign data</p>
                          </div>
                          <Link2 className="w-5 h-5 text-blue-600" />
                        </div>
                        <Button 
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          onClick={handleFacebookLogin}
                          disabled={connectingFb}
                          data-testid="connect-facebook-btn"
                        >
                          {connectingFb ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Connecting...
                            </>
                          ) : (
                            <>
                              <LogIn className="w-4 h-4 mr-2" />
                              Connect Facebook Account
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button 
                          className="flex-1 bg-blue-600 hover:bg-blue-700"
                          onClick={() => window.open('https://business.facebook.com/adsmanager', '_blank')}
                          data-testid="open-ads-manager-btn"
                        >
                          <Facebook className="w-4 h-4 mr-2" />
                          Open Ads Manager
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={handleFacebookLogin}
                          data-testid="reconnect-facebook-btn"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* A/B Testing Tool */}
            {activeTool === "ab-testing" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                    <Shuffle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">A/B Testing Tool</h2>
                    <p className="text-gray-600">Test different ad variations to find winners</p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h4 className="font-medium text-purple-800 mb-2">What to A/B Test?</h4>
                    <ul className="text-sm text-purple-700 space-y-1">
                      <li>• Headlines and ad copy</li>
                      <li>• Images and videos</li>
                      <li>• Call-to-action buttons</li>
                      <li>• Audience targeting</li>
                      <li>• Ad placements</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold mb-4">Create A/B Test</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 border-2 border-dashed border-purple-300 rounded-lg bg-purple-50">
                        <h4 className="font-medium mb-3 text-purple-700">Variant A (Control)</h4>
                        <Input placeholder="Ad headline A..." className="mb-2" />
                        <Input placeholder="CTA text A..." />
                      </div>
                      <div className="p-4 border-2 border-dashed border-pink-300 rounded-lg bg-pink-50">
                        <h4 className="font-medium mb-3 text-pink-700">Variant B (Test)</h4>
                        <Input placeholder="Ad headline B..." className="mb-2" />
                        <Input placeholder="CTA text B..." />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Test Duration</label>
                    <Select defaultValue="7days">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3days">3 Days</SelectItem>
                        <SelectItem value="7days">7 Days (Recommended)</SelectItem>
                        <SelectItem value="14days">14 Days</SelectItem>
                        <SelectItem value="30days">30 Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button className="w-full bg-purple-600 hover:bg-purple-700">
                    <Shuffle className="w-4 h-4 mr-2" />
                    Start A/B Test
                  </Button>

                  {/* Sample Results */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium mb-4">Previous Test Results</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-white rounded-lg border-2 border-green-500">
                        <Badge className="bg-green-100 text-green-700 mb-2">Winner</Badge>
                        <p className="font-medium">Variant A</p>
                        <p className="text-sm text-gray-600 mb-2">"Get 50% Off Today!"</p>
                        <div className="text-sm space-y-1">
                          <p>CTR: <span className="font-semibold text-green-600">4.8%</span></p>
                          <p>Conversions: <span className="font-semibold">156</span></p>
                        </div>
                      </div>
                      <div className="p-4 bg-white rounded-lg border">
                        <Badge variant="outline" className="mb-2">Runner-up</Badge>
                        <p className="font-medium">Variant B</p>
                        <p className="text-sm text-gray-600 mb-2">"Limited Time Offer"</p>
                        <div className="text-sm space-y-1">
                          <p>CTR: <span className="font-semibold">3.2%</span></p>
                          <p>Conversions: <span className="font-semibold">98</span></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Conversion Tracking */}
            {activeTool === "conversion-tracking" && (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Conversion Tracking</h2>
                      <p className="text-gray-600">Track and optimize your ad conversions</p>
                    </div>
                  </div>
                  {isDemo && (
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Demo Data
                    </Badge>
                  )}
                </div>

                <div className="space-y-6">
                  {/* Conversion Events from API */}
                  <div>
                    <h3 className="font-semibold mb-4">Conversion Events</h3>
                    <div className="space-y-3">
                      {[
                        { 
                          event: "Purchase", 
                          count: dashboardData?.summary?.conversions || 245, 
                          value: `₹${(dashboardData?.summary?.conversion_value || 12450).toLocaleString()}`, 
                          icon: "💰" 
                        },
                        { 
                          event: "Add to Cart", 
                          count: Math.round((dashboardData?.summary?.clicks || 4523) * 0.2), 
                          value: "-", 
                          icon: "🛒" 
                        },
                        { 
                          event: "Lead Form", 
                          count: Math.round((dashboardData?.summary?.conversions || 245) * 0.64), 
                          value: "-", 
                          icon: "📝" 
                        },
                        { 
                          event: "Page View", 
                          count: dashboardData?.summary?.impressions || 15420, 
                          value: "-", 
                          icon: "👁️" 
                        },
                        { 
                          event: "Link Click", 
                          count: dashboardData?.summary?.clicks || 4523, 
                          value: "-", 
                          icon: "🔗" 
                        }
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-4 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{item.icon}</span>
                            <div>
                              <p className="font-medium">{item.event}</p>
                              <p className="text-sm text-gray-500">{item.count.toLocaleString()} conversions</p>
                            </div>
                          </div>
                          {item.value !== "-" && (
                            <Badge className="bg-green-100 text-green-700">{item.value}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Pixel Setup */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium mb-3">Facebook Pixel Setup</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Add this code to your website's &lt;head&gt; section to track conversions:
                    </p>
                    <div className="relative">
                      <pre className="text-xs bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto">
{`<!-- Facebook Pixel Code -->
<script>
!function(f,b,e,v,n,t,s){...}
fbq('init', 'YOUR_PIXEL_ID');
fbq('track', 'PageView');
</script>`}
                      </pre>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="absolute top-2 right-2"
                        onClick={() => toast.success("Pixel code copied!")}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Conversion Funnel */}
                  <div>
                    <h4 className="font-medium mb-4">Conversion Funnel</h4>
                    <div className="space-y-2">
                      {[
                        { step: "Impressions", count: dashboardData?.summary?.impressions || 125000, width: "100%" },
                        { step: "Reach", count: dashboardData?.summary?.reach || 89250, width: "75%" },
                        { step: "Clicks", count: dashboardData?.summary?.clicks || 4500, width: "40%" },
                        { step: "Add to Cart", count: Math.round((dashboardData?.summary?.clicks || 4500) * 0.2), width: "20%" },
                        { step: "Purchase", count: dashboardData?.summary?.conversions || 245, width: "10%" }
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <span className="w-24 text-sm text-gray-600">{item.step}</span>
                          <div className="flex-1 h-8 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-end pr-3"
                              style={{ width: item.width }}
                            >
                              <span className="text-xs text-white font-medium">{item.count.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => window.open('https://business.facebook.com/events_manager', '_blank')}
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Setup Conversion Tracking
                  </Button>
                </div>
              </Card>
            )}

            {/* API Test Tool (For Meta App Review) */}
            {activeTool === "api-test" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <RefreshCw className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">API Test Tool</h2>
                    <p className="text-gray-600">Test Marketing API for Meta App Review</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Instructions */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-2">Meta App Review ke liye:</h4>
                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                      <li>Facebook Graph API Explorer se Access Token generate karo</li>
                      <li>Permissions select karo: <code className="bg-blue-100 px-1">ads_read</code>, <code className="bg-blue-100 px-1">ads_management</code></li>
                      <li>Token niche paste karo aur "Test API" click karo</li>
                      <li>SUCCESS aane par 24 hours wait karo</li>
                      <li>Advanced Access button active ho jayega</li>
                    </ol>
                  </div>

                  {/* Token Input */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Facebook Access Token *
                      </label>
                      <Input
                        value={testToken}
                        onChange={(e) => setTestToken(e.target.value)}
                        placeholder="Paste your Facebook User Access Token here..."
                        className="font-mono text-sm"
                        data-testid="fb-test-token-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ad Account ID (Optional)
                      </label>
                      <Input
                        value={testAdAccountId}
                        onChange={(e) => setTestAdAccountId(e.target.value)}
                        placeholder="e.g., 3209527712698252"
                        data-testid="fb-test-account-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty to auto-detect from your account</p>
                    </div>
                    
                    <Button
                      onClick={testFacebookApi}
                      disabled={testingApi || !testToken.trim()}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      data-testid="fb-test-api-btn"
                    >
                      {testingApi ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Testing API Calls...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Test Marketing API
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Test Results */}
                  {testResults && (
                    <div className="space-y-4">
                      <h4 className="font-semibold">Test Results:</h4>
                      
                      {testResults.summary && (
                        <div className="grid grid-cols-3 gap-4">
                          <div className="p-3 bg-gray-50 rounded-lg text-center">
                            <p className="text-sm text-gray-500">Total Tests</p>
                            <p className="text-xl font-bold">{testResults.summary.total_tests}</p>
                          </div>
                          <div className="p-3 bg-green-50 rounded-lg text-center">
                            <p className="text-sm text-green-600">Passed</p>
                            <p className="text-xl font-bold text-green-600">{testResults.summary.passed}</p>
                          </div>
                          <div className="p-3 bg-red-50 rounded-lg text-center">
                            <p className="text-sm text-red-600">Failed</p>
                            <p className="text-xl font-bold text-red-600">{testResults.summary.failed}</p>
                          </div>
                        </div>
                      )}

                      {testResults.test_results?.map((test, idx) => (
                        <div 
                          key={idx} 
                          className={`p-4 rounded-lg border ${
                            test.status === "SUCCESS" 
                              ? "bg-green-50 border-green-200" 
                              : "bg-red-50 border-red-200"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium">{test.test}</span>
                            <Badge className={
                              test.status === "SUCCESS" 
                                ? "bg-green-100 text-green-700" 
                                : "bg-red-100 text-red-700"
                            }>
                              {test.status}
                            </Badge>
                          </div>
                          {test.message && (
                            <p className="text-sm text-green-600 mb-2">{test.message}</p>
                          )}
                          {test.response && (
                            <pre className="text-xs bg-white p-2 rounded overflow-x-auto max-h-32">
                              {JSON.stringify(test.response, null, 2)}
                            </pre>
                          )}
                          {test.error && (
                            <pre className="text-xs text-red-600 bg-white p-2 rounded overflow-x-auto">
                              {typeof test.error === 'string' ? test.error : JSON.stringify(test.error, null, 2)}
                            </pre>
                          )}
                        </div>
                      ))}

                      {testResults.summary?.next_steps && (
                        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                          <p className="text-sm text-yellow-800">
                            <strong>Next Steps:</strong> {testResults.summary.next_steps}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Get Token Link */}
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => window.open('https://developers.facebook.com/tools/explorer/', '_blank')}
                  >
                    <Facebook className="w-4 h-4 mr-2" />
                    Open Graph API Explorer (Get Token)
                  </Button>
                </div>
              </Card>
            )}

            {/* Meta Ads Creative Downloader */}
            {activeTool === "meta-ad-downloader" && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                    <Download className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Meta Ads Creative Downloader</h2>
                    <p className="text-gray-600">Download creatives from Meta Ads Library</p>
                  </div>
                </div>

                {/* Info Banner */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-800 font-medium">How to use:</p>
                      <ol className="text-sm text-blue-700 mt-1 list-decimal list-inside space-y-1">
                        <li>Go to <a href="https://www.facebook.com/ads/library" target="_blank" rel="noopener noreferrer" className="underline">Meta Ads Library</a></li>
                        <li>Find an ad and click on it to view details</li>
                        <li>Copy the URL from your browser</li>
                        <li>Paste it here to extract the creative</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Input Section */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Meta Ads Library URL</label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="https://www.facebook.com/ads/library/?id=..."
                        value={metaAdUrl}
                        onChange={(e) => setMetaAdUrl(e.target.value)}
                        className="flex-1"
                        data-testid="meta-ad-url-input"
                      />
                      <Button
                        onClick={handleMetaAdDownload}
                        disabled={loadingMetaAd || !metaAdUrl.trim()}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="extract-creative-btn"
                      >
                        {loadingMetaAd ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Extracting...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Extract Creative
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Loading State */}
                  {loadingMetaAd && (
                    <div className="p-6 bg-gray-50 rounded-lg border text-center">
                      <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-600" />
                      <p className="text-gray-600">Extracting creative from Meta Ads Library...</p>
                      <p className="text-xs text-gray-500 mt-1">This may take up to 10 seconds</p>
                    </div>
                  )}

                  {/* Error State */}
                  {metaAdError && !loadingMetaAd && (
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-red-800">Extraction Failed</p>
                          <p className="text-sm text-red-600 mt-1">{metaAdError}</p>
                          <div className="mt-3 text-sm text-red-700">
                            <p className="font-medium">Possible reasons:</p>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                              <li>The ad may be restricted or region-locked</li>
                              <li>The ad may have been removed</li>
                              <li>Invalid URL format</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Results */}
                  {metaAdResult && metaAdResult.success && !loadingMetaAd && (
                    <div className="space-y-4">
                      {/* Preview Card */}
                      <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-2 mb-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-800">Creative Found!</span>
                          {metaAdResult.cached && (
                            <Badge variant="outline" className="text-xs">Cached</Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-3 mb-4">
                          <Badge className={metaAdResult.media_type === 'video' ? 'bg-purple-600' : 'bg-blue-600'}>
                            {metaAdResult.media_type === 'video' ? (
                              <><Video className="w-3 h-3 mr-1" /> Video</>
                            ) : (
                              <><ImageIcon className="w-3 h-3 mr-1" /> Image</>
                            )}
                          </Badge>
                          {metaAdResult.page_name && (
                            <span className="text-sm text-gray-600">by {metaAdResult.page_name}</span>
                          )}
                        </div>

                        {/* Media Preview */}
                        <div className="rounded-lg overflow-hidden bg-gray-100 mb-4 relative">
                          {metaAdResult.media_type === 'video' ? (
                            <>
                              {/* Video thumbnail or placeholder */}
                              {metaAdResult.thumbnail_url ? (
                                <img 
                                  src={metaAdResult.thumbnail_url} 
                                  alt="Video Thumbnail" 
                                  className="w-full max-h-[400px] object-contain"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div 
                                className={`${metaAdResult.thumbnail_url ? 'hidden' : 'flex'} w-full h-[300px] bg-gradient-to-br from-purple-100 to-blue-100 items-center justify-center flex-col`}
                              >
                                <Video className="w-16 h-16 text-purple-500 mb-3" />
                                <p className="text-purple-700 font-medium">Video Ready for Download</p>
                                <p className="text-purple-500 text-sm mt-1">Preview not available due to CORS restrictions</p>
                              </div>
                              {/* Play button overlay */}
                              {metaAdResult.thumbnail_url && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
                                  <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                                    <div className="w-0 h-0 border-t-8 border-t-transparent border-l-12 border-l-purple-600 border-b-8 border-b-transparent ml-1"></div>
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <img 
                              src={metaAdResult.media_url} 
                              alt="Ad Creative" 
                              className="w-full max-h-[400px] object-contain"
                              data-testid="image-preview"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '';
                                e.target.alt = 'Image preview not available';
                                e.target.className = 'w-full h-[200px] bg-gray-200 flex items-center justify-center';
                              }}
                            />
                          )}
                        </div>

                        {/* Ad Text */}
                        {metaAdResult.ad_text && (
                          <div className="p-3 bg-white rounded-lg border mb-4">
                            <p className="text-xs text-gray-500 mb-1">Ad Copy</p>
                            <p className="text-sm text-gray-700">{metaAdResult.ad_text.slice(0, 300)}{metaAdResult.ad_text.length > 300 ? '...' : ''}</p>
                          </div>
                        )}

                        {/* Download Buttons */}
                        <div className="flex gap-3">
                          <Button
                            onClick={handleDownloadCreative}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            data-testid="download-btn"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download {metaAdResult.media_type === 'video' ? 'MP4' : 'Image'}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              copyToClipboard(metaAdResult.media_url);
                            }}
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy URL
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => window.open(metaAdResult.media_url, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Example URLs */}
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm font-medium text-gray-700 mb-2">Example URL formats:</p>
                  <div className="space-y-2 text-xs text-gray-500 font-mono bg-gray-50 p-3 rounded">
                    <p>https://www.facebook.com/ads/library/?id=123456789012345</p>
                    <p>https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=IN&view_all_page_id=123456789</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Placeholder for other tools */}
            {!["audience-builder", "ad-creative", "roi-calculator", "campaign-manager", "ab-testing", "conversion-tracking", "api-test", "meta-ad-downloader"].includes(activeTool) && (
              <Card className="p-6 text-center">
                <div className="py-12">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    {facebookAdsTools.find(t => t.id === activeTool)?.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {facebookAdsTools.find(t => t.id === activeTool)?.name}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {facebookAdsTools.find(t => t.id === activeTool)?.description}
                  </p>
                  <Badge>Coming Soon</Badge>
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Related Tools Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <RelatedTools currentToolId={activeTool} category="ads" />
        </div>
      </div>
    </div>
  );
}

export default FacebookAdsToolsContent;
